import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchAllIdClients,
  fetchAllMarkets,
  fetchRefresh,
  fetchSKUCompanies,
  fetchCompanyAllBrands,
  appendGeoscopeMarkets,
} from "../api/internal_tool_endpoints";

const initialState = {
  loading: false,
  error: null,

  // Client data
  clientIds: [],
  clientIdsLoading: false,
  clientIdsError: null,

  // Market data
  markets: [],
  marketsLoading: false,
  marketsError: null,

  // Refresh data
  refreshData: null,
  refreshLoading: false,
  refreshError: null,
  // HTTP status behind refreshError. 404 = the client has no refresh data,
  // which is an empty state rather than a failure.
  refreshErrorStatus: null,

  // SKU Companies data (stored by country)
  skuCompanies: {},
  skuCompaniesLoading: false,
  skuCompaniesError: null,

  // Company Brands data (with subbrands tree structure)
  companyBrands: {},
  companyBrandsLoading: false,
  companyBrandsError: null,

  // Selected client for filtering
  selectedClientId: null,
};

// Async thunks for API calls
export const fetchAllIdClientsAction = createAsyncThunk(
  "internalTool/fetchAllIdClients",
  async (_, { rejectWithValue }) => {
    try {
      const clientIds = await fetchAllIdClients();
      return clientIds;
    } catch (error) {
      console.error("Failed to fetch client IDs:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllMarketsAction = createAsyncThunk(
  "internalTool/fetchAllMarkets",
  async ({ id_client }, { rejectWithValue }) => {
    try {
      if (!id_client) {
        throw new Error("Client ID is required");
      }
      const markets = await fetchAllMarkets({ id_client });
      return { id_client, markets };
    } catch (error) {
      console.error("Failed to fetch markets:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchRefreshAction = createAsyncThunk(
  "internalTool/fetchRefresh",
  async ({ id_client }, { rejectWithValue }) => {
    try {
      if (!id_client) {
        throw new Error("Client ID is required");
      }
      const refreshData = await fetchRefresh({ id_client });
      return { id_client, refreshData };
    } catch (error) {
      console.error("Failed to fetch refresh data:", error);
      // `status` lets the caller tell "this client has no refresh data" (404)
      // from a retryable failure; consumers read `.message` for display.
      return rejectWithValue({
        message: error.message,
        status: error.status,
      });
    }
  }
);

export const fetchSKUCompaniesAction = createAsyncThunk(
  "internalTool/fetchSKUCompanies",
  async ({ id_client, country }, { rejectWithValue }) => {
    try {
      if (!id_client) {
        throw new Error("Client ID is required");
      }
      if (!country) {
        throw new Error("Country is required");
      }
      const companiesData = await fetchSKUCompanies({ id_client, country });
      return { id_client, country, companiesData };
    } catch (error) {
      console.error("Failed to fetch SKU companies:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCompanyAllBrandsAction = createAsyncThunk(
  "internalTool/fetchCompanyAllBrands",
  async ({ id_client, country, company }, { rejectWithValue }) => {
    try {
      if (!id_client) {
        throw new Error("Client ID is required");
      }
      if (!country) {
        throw new Error("Country is required");
      }
      if (!company) {
        throw new Error("Company is required");
      }
      const brandsData = await fetchCompanyAllBrands({
        id_client,
        country,
        company,
      });
      console.log(brandsData);

      return { id_client, country, company, brandsData };
    } catch (error) {
      console.error("Failed to fetch company brands:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Add markets to a workspace's geoscope, creating the document when absent, so
// Create Workspace and Add Market share one idempotent write.
//
// Rejects with `{ message, status }` rather than a bare string: Add Market
// compensates its geoscope write only when the server actually answered, and
// `status` is the only thing that distinguishes that from an unknown outcome.
export const appendGeoscopeMarketsAction = createAsyncThunk(
  "internalTool/appendGeoscopeMarkets",
  async ({ id_client, regions_info }, { rejectWithValue }) => {
    try {
      if (!id_client) {
        throw new Error("Client ID is required");
      }
      if (!regions_info) {
        throw new Error("Regions info is required");
      }
      return await appendGeoscopeMarkets({ id_client, regions_info });
    } catch (error) {
      console.error("Failed to append geoscope markets:", error);
      return rejectWithValue({
        message: error.message,
        status: error.status,
      });
    }
  }
);

const internalToolSlice = createSlice({
  name: "internalTool",
  initialState,
  reducers: {
    // Synchronous actions
    setSelectedClientId: (state, action) => {
      state.selectedClientId = action.payload;
      // Clear markets, refresh data, SKU companies, and company brands when client changes
      state.markets = [];
      state.refreshData = null;
      // Cleared with the data: a stale 404 status would otherwise report "this
      // client has no refresh periods" about the previous client.
      state.refreshError = null;
      state.refreshErrorStatus = null;
      state.skuCompanies = {};
      state.companyBrands = {};
    },
    clearError: (state) => {
      state.error = null;
      state.clientIdsError = null;
      state.marketsError = null;
      state.refreshError = null;
      state.refreshErrorStatus = null;
      state.skuCompaniesError = null;
      state.companyBrandsError = null;
    },
    clearClientIdsError: (state) => {
      state.clientIdsError = null;
    },
    clearMarketsError: (state) => {
      state.marketsError = null;
    },
    clearRefreshError: (state) => {
      state.refreshError = null;
      state.refreshErrorStatus = null;
    },
    clearSKUCompaniesError: (state) => {
      state.skuCompaniesError = null;
    },
    clearCompanyBrandsError: (state) => {
      state.companyBrandsError = null;
    },
    clearMarkets: (state) => {
      state.markets = [];
      state.marketsError = null;
    },
    clearRefreshData: (state) => {
      state.refreshData = null;
      state.refreshError = null;
      state.refreshErrorStatus = null;
    },
    clearSKUCompanies: (state) => {
      state.skuCompanies = {};
      state.skuCompaniesError = null;
    },
    clearCompanyBrands: (state) => {
      state.companyBrands = {};
      state.companyBrandsError = null;
    },
    resetState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all client IDs
      .addCase(fetchAllIdClientsAction.pending, (state) => {
        state.clientIdsLoading = true;
        state.clientIdsError = null;
      })
      .addCase(fetchAllIdClientsAction.fulfilled, (state, action) => {
        state.clientIdsLoading = false;
        state.clientIds = action.payload;
        state.clientIdsError = null;
      })
      .addCase(fetchAllIdClientsAction.rejected, (state, action) => {
        state.clientIdsLoading = false;
        state.clientIdsError = action.payload;
        state.clientIds = [];
      })

      // Fetch markets for client
      .addCase(fetchAllMarketsAction.pending, (state) => {
        state.marketsLoading = true;
        state.marketsError = null;
      })
      .addCase(fetchAllMarketsAction.fulfilled, (state, action) => {
        state.marketsLoading = false;
        state.markets = action.payload.markets;
        state.marketsError = null;
      })
      .addCase(fetchAllMarketsAction.rejected, (state, action) => {
        state.marketsLoading = false;
        state.marketsError = action.payload;
        state.markets = [];
      })

      // Fetch refresh data for client
      .addCase(fetchRefreshAction.pending, (state) => {
        state.refreshLoading = true;
        state.refreshError = null;
        state.refreshErrorStatus = null;
      })
      .addCase(fetchRefreshAction.fulfilled, (state, action) => {
        state.refreshLoading = false;
        state.refreshData = action.payload.refreshData;
        state.refreshError = null;
        state.refreshErrorStatus = null;
      })
      .addCase(fetchRefreshAction.rejected, (state, action) => {
        state.refreshLoading = false;
        state.refreshError = action.payload?.message ?? action.payload;
        // 404 means the client has no refresh data at all, which the wizard
        // renders as an empty state rather than a failure. Kept as a status
        // rather than matched out of the message, which silently breaks on a
        // reword (see src/utils/exceptions.py in the API for the same reason).
        state.refreshErrorStatus = action.payload?.status ?? null;
        state.refreshData = null;
      })

      // Fetch SKU companies for client and country
      .addCase(fetchSKUCompaniesAction.pending, (state) => {
        state.skuCompaniesLoading = true;
        state.skuCompaniesError = null;
      })
      .addCase(fetchSKUCompaniesAction.fulfilled, (state, action) => {
        state.skuCompaniesLoading = false;
        console.log("SKU Companies Action Payload:", action.payload);

        const { country, companiesData } = action.payload;
        // Create a new object to avoid Immer issues
        state.skuCompanies = {
          ...state.skuCompanies,
          [country]: companiesData,
        };
        state.skuCompaniesError = null;
      })
      .addCase(fetchSKUCompaniesAction.rejected, (state, action) => {
        state.skuCompaniesLoading = false;
        state.skuCompaniesError = action.payload;
      })

      // Fetch company brands tree for client, country and company
      .addCase(fetchCompanyAllBrandsAction.pending, (state) => {
        state.companyBrandsLoading = true;
        state.companyBrandsError = null;
      })
      .addCase(fetchCompanyAllBrandsAction.fulfilled, (state, action) => {
        state.companyBrandsLoading = false;
        const { company, brandsData } = action.payload;
        // Store brands data by company name for easy lookup using Object.assign for Immer compatibility
        Object.assign(state.companyBrands, { [company]: brandsData });
        state.companyBrandsError = null;
      })
      .addCase(fetchCompanyAllBrandsAction.rejected, (state, action) => {
        state.companyBrandsLoading = false;
        state.companyBrandsError = action.payload;
      });
  },
});

export const {
  setSelectedClientId,
  clearError,
  clearClientIdsError,
  clearMarketsError,
  clearRefreshError,
  clearSKUCompaniesError,
  clearCompanyBrandsError,
  clearMarkets,
  clearRefreshData,
  clearSKUCompanies,
  clearCompanyBrands,
  resetState,
} = internalToolSlice.actions;

export default internalToolSlice.reducer;
